import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';

interface UseCarouselProps {
  loop: boolean;
}

interface UseCarouselReturn {
  emblaRef: (node: HTMLElement | null) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  scrollPrev: () => void;
  scrollNext: () => void;
}

export const useCarousel = ({ loop }: UseCarouselProps): UseCarouselReturn => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    slidesToScroll: 1,
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const scrollByStep = (stepDirection: -1 | 1): void => {
    if (!emblaApi) return;

    const visibleSlides = emblaApi.slidesInView().length;
    const slideStep = Math.max(1, visibleSlides - 1);
    const currentSnap = emblaApi.selectedScrollSnap();
    const totalSnaps = emblaApi.scrollSnapList().length;
    const targetSnap = Math.min(
      totalSnaps - 1,
      Math.max(0, currentSnap + slideStep * stepDirection)
    );

    emblaApi.scrollTo(targetSnap);
  };

  const scrollPrev = (): void => scrollByStep(-1);

  const scrollNext = (): void => scrollByStep(1);

  useEffect(() => {
    if (!emblaApi) return;

    const updateState = (): void => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', updateState).on('reInit', updateState);
    updateState();

    return (): void => {
      emblaApi.off('select', updateState).off('reInit', updateState);
    };
  }, [emblaApi]);

  return {
    emblaRef,
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
  };
};
